import BookingModal from "@components/booking/BookingModal";
import BookingsTable from "@components/booking/BookingsTable";
import Loading from "@components/loading/Loading";
import { useBookingContext } from "@providers/BookingProvider";
import { useConfirmationContext } from "@providers/ConfirmationProvider";
import { useNotificationContext } from "@providers/NotificationProvider";
import {
  useDeleteBooking,
  useGetAllBookings,
} from "@store/actions/bookingActions";
import { memo } from "react";
import { useCallback, useEffect, useState } from "react";
import { Route, Switch, useRouteMatch, useLocation } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import BookingPage from "./BookingPage";
import StatCard from "@components/stats/StatCard";
import { useGetStats } from "@store/actions/analyticsActions";
import { Box, Grid } from "@material-ui/core";

function BookingsPage() {
  const { path } = useRouteMatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [poll, setPoll] = useState(true);
  const { setBooking, setBookings, setItemInput } = useBookingContext();
  const { loading, error, data, startPolling, stopPolling } = useGetAllBookings(
    10000
  ); // update every 10 seconds
  const { data: statsData } = useGetStats();
  const { showNotification } = useNotificationContext();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const [deleteBooking, { loading: deleting }] = useDeleteBooking();

  useEffect(() => {
    if (data?.bookings) {
      setBookings(data.bookings);
    }
  }, [data]);

  useUpdateEffect(() => {
    if (poll) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [poll]);

  useEffect(() => {
    if (location.pathname === "/bookings") {
      setPoll(true);
    } else {
      setPoll(false);
    }
  }, [location]);

  const newBooking = useCallback(() => {
    setBooking({});
    setItemInput({});
    setOpen(true);
  }, [setBooking]);

  const onEdit = useCallback(
    (booking) => {
      setBooking(booking);
      setOpen(true);
    },
    [setBooking]
  );

  const onDelete = useCallback(
    ({ reference, id }) => {
      openConfirmation({
        title: `Delete ${reference}`,
        body: `Deleting ${reference} can not be undone, are you sure you want to continue?`,
        loading: () => deleting,
        callback: () =>
          deleteBooking({
            variables: {
              id,
            },
          }).then(() => {
            showNotification(`${reference} Deleted!`);
            closeConfirmation();
          }),
      });
    },
    [deleting]
  );

  const onSave = useCallback(
    ({ reference }) => {
      showNotification(`${reference} Saved!`);
      setOpen(false);
    },
    [showNotification]
  );

  if (loading) return <Loading cover={true} />;
  if (error) return <div>{JSON.stringify(error)}</div>;

  return (
    <>
      <Switch>
        <Route path="/bookings/:bookingId">
          <BookingPage />
        </Route>
        <Route exact path={path}>
          <>
          <Box 
            mb={4}
            display="flex"
            flexWrap="wrap"
            style={{ 
             gap: '16px'
            }}
          >

              <StatCard 
                title="Bookings"
                last7Days={statsData.stats.bookingsLast7Days}
                last7DaysYoy={statsData.stats.bookingsLast7DaysYoy}
                last30Days={statsData.stats.bookingsLast30Days}
                last30DaysYoy={statsData.stats.bookingsLast30DaysYoy}
                monthToDate={statsData.stats.bookingsMonthToDate}
                monthToDateLastYear={statsData.stats.bookingsMonthToDateLastYear}
                monthLastYearTotal={statsData.stats.bookingsMonthLastYearTotal}
              />

              <StatCard 
                title="Quotes Sent"
                last7Days={statsData.stats.quotesSentLast7Days}
                last7DaysYoy={statsData.stats.quotesSentLast7DaysYoy}
                last30Days={statsData.stats.quotesSentLast30Days}
                last30DaysYoy={statsData.stats.quotesSentLast30DaysYoy}
                monthToDate={statsData.stats.quotesSentMonthToDate}
                monthToDateLastYear={statsData.stats.quotesSentMonthToDateLastYear}
                monthLastYearTotal={statsData.stats.quotesSentMonthLastYearTotal}
              />


              <StatCard 
                title="Quote Rate"
                last7Days={statsData.stats.quoteRateLast7Days}
                last7DaysYoy={statsData.stats.quoteRateLast7DaysYoy}
                last30Days={statsData.stats.quoteRateLast30Days}
                last30DaysYoy={statsData.stats.quoteRateLast30DaysYoy}
                monthToDate={statsData.stats.quoteRateMonthToDate}
                monthToDateLastYear={statsData.stats.quoteRateMonthToDateLastYear}
                monthLastYearTotal={statsData.stats.quoteRateMonthLastYearTotal}
                isRate={true}
              />

              <StatCard 
                title="Closed Bookings"
                last7Days={statsData.stats.closedBookingsLast7Days}
                last7DaysYoy={statsData.stats.closedBookingsLast7DaysYoy}
                last30Days={statsData.stats.closedBookingsLast30Days}
                last30DaysYoy={statsData.stats.closedBookingsLast30DaysYoy}
                monthToDate={statsData.stats.closedBookingsMonthToDate}
                monthToDateLastYear={statsData.stats.closedBookingsMonthToDateLastYear}
                monthLastYearTotal={statsData.stats.closedBookingsMonthLastYearTotal}
              />
              <StatCard 
                title="Close Rate"
                last7Days={statsData.stats.closeRateLast7Days}
                last7DaysYoy={statsData.stats.closeRateLast7DaysYoy}
                last30Days={statsData.stats.closeRateLast30Days}
                last30DaysYoy={statsData.stats.closeRateLast30DaysYoy}
                monthToDate={statsData.stats.closeRateMonthToDate}
                monthToDateLastYear={statsData.stats.closeRateMonthToDateLastYear}
                monthLastYearTotal={statsData.stats.closeRateMonthLastYearTotal}
                isRate={true}
              />
          </Box>
          <BookingsTable
            onEdit={onEdit}
            onDelete={onDelete}
            onNew={newBooking}
            poll={poll}
            setPoll={setPoll}
          />
          </>
        </Route>
      </Switch>

      <BookingModal open={open} setOpen={setOpen} onSave={onSave} />
    </>
  );
}

export default memo(BookingsPage);
